/**
 * Hook for streaming agent steps from a browser farm instance via SSE.
 *
 * The connection itself lives in `agent-stream-connection`, shared by URL:
 * several components read the same session's steps at once (the inline card's
 * steps viewer, the expanded layover's, and `BrowserLiveView`'s pre-cache) and
 * one SSE per session is the budget.
 *
 * @module browser-automation/hooks/use-agent-stream
 */

import { useEffect, useRef, useState } from 'react';

import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import { useAuth } from '@/shared/auth';

import {
  subscribeToAgentStream,
  type IAgentStreamSnapshot,
} from './agent-stream-connection';
import { type EAgentStatus, type IAgentStep } from '../types';

export {
  streamCache,
  fatalUrlCache,
  resetAgentStreams,
  countAgentStreams,
  countAgentStreamListeners,
  AGENT_STREAM_LINGER_MS,
} from './agent-stream-connection';

/**
 * Options for the useAgentStream hook.
 */
export interface IUseAgentStreamOptions {
  /** Maximum number of steps to retain. Oldest steps are discarded when exceeded. */
  maxSteps?: number;
}

/**
 * Return value of the useAgentStream hook.
 */
export interface IUseAgentStreamReturn {
  /** Accumulated agent steps. */
  steps: IAgentStep[];
  /** Current agent status reported by the server. */
  agentStatus: EAgentStatus | null;
  /** Whether the SSE connection is currently attempting to connect. */
  isConnecting: boolean;
  /** Whether the SSE connection is established and receiving messages. */
  isConnected: boolean;
  /** Error message if the connection failed or the server reported an error. */
  error: string | null;
}

/** Default maximum number of steps to keep in memory. */
const DEFAULT_MAX_STEPS = 500;

const IDLE_SNAPSHOT: IAgentStreamSnapshot = {
  steps: [],
  agentStatus: null,
  isConnecting: false,
  isConnected: false,
  error: null,
};

/**
 * Streams agent steps from a browser farm instance via SSE.
 *
 * @param streamUrl - HTTP URL for the agent stream SSE endpoint, or null to skip connection.
 * @param options - Optional configuration.
 * @returns Reactive state of the agent stream connection and steps.
 *
 * @example
 * ```tsx
 * const { steps, agentStatus, isConnecting, error } = useAgentStream(
 *   'http://bf.example.com/agent/stream/browser-123',
 * );
 * ```
 */
export function useAgentStream(
  streamUrl: string | null,
  options: IUseAgentStreamOptions = {},
): IUseAgentStreamReturn {
  'use no memo';

  const { maxSteps = DEFAULT_MAX_STEPS } = options;
  const { getAccessTokenSilently } = useAuth();
  // Store in ref so the subscribe effect doesn't re-run on token refresh.
  // The token is only needed at connection time, and the stream reads the
  // latest reference from the ref at that point.
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;

  const [snapshot, setSnapshot] = useState<IAgentStreamSnapshot>(IDLE_SNAPSHOT);

  // Kept in a ref so a changed cap does not tear down the connection.
  const maxStepsRef = useRef(maxSteps);
  maxStepsRef.current = maxSteps;

  // eslint-disable-next-line no-restricted-syntax -- reason: SSE subscription lifecycle
  useEffect(() => {
    if (!streamUrl) {
      setSnapshot(IDLE_SNAPSHOT);
      return;
    }
    const headers: Record<string, string> = isE2ETestMode()
      ? { 'X-E2E-Test-Mode': 'true' }
      : {};
    return subscribeToAgentStream(
      streamUrl,
      () => getAccessTokenSilentlyRef.current(),
      headers,
      maxStepsRef.current,
      setSnapshot,
    );
  }, [streamUrl]);

  return snapshot;
}
